import type {
  DashboardErrorCode,
  DashboardErrorResponse,
  DashboardRecoveryAction,
} from '@shared/types/dashboard-errors';

export interface DashboardUiError {
  message: string;
  code: DashboardErrorCode | null;
  recoveryActions: DashboardRecoveryAction[];
  details?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isResponseLike = (value: unknown): value is Response =>
  isRecord(value) &&
  typeof value.clone === 'function' &&
  typeof value.json === 'function' &&
  typeof value.text === 'function';

const isDashboardErrorResponse = (value: unknown): value is DashboardErrorResponse =>
  isRecord(value) &&
  typeof value.error === 'string' &&
  typeof value.errorCode === 'string' &&
  (value.recoveryActions === undefined ||
    (Array.isArray(value.recoveryActions) &&
      value.recoveryActions.every((entry) => typeof entry === 'string')));

const normalize = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const looksTechnicalMessage = (value: string | null): boolean => {
  if (!value) return true;

  const normalizedValue = normalize(value);
  return (
    value.includes('{') ||
    value.includes('}') ||
    /"error"|stack|trace|pgrst|23505|schema|syntax/i.test(value) ||
    normalizedValue.includes('internal server error') ||
    normalizedValue.includes('input validation failed') ||
    normalizedValue.includes('expected number') ||
    normalizedValue.includes('expected boolean') ||
    normalizedValue.includes('received string') ||
    normalizedValue.includes('invalid_type') ||
    normalizedValue.includes('zod') ||
    normalizedValue.includes('cannot read properties of') ||
    normalizedValue.includes('failed to fetch') ||
    normalizedValue.includes('failed (') ||
    normalizedValue.includes('dashboard planner') ||
    normalizedValue.includes('api error') ||
    normalizedValue.includes('non-2xx') ||
    /\b[a-z]+(?:_[a-z]+){2,}\b/.test(value) ||
    normalizedValue.includes('openai') ||
    normalizedValue.includes('anthropic')
  );
};

export const toSafeUiMessage = (
  message: string | null | undefined,
  fallbackMessage: string
): string =>
  looksTechnicalMessage(message ?? null) ? fallbackMessage : (message ?? fallbackMessage);

const readResponsePayload = async (response: Response): Promise<unknown | null> => {
  try {
    return await response.clone().json();
  } catch {
    const text = await response
      .clone()
      .text()
      .catch(() => '');
    if (!text) return null;

    try {
      return JSON.parse(text) as unknown;
    } catch {
      return { error: text };
    }
  }
};

const buildUiErrorFromPayload = (
  payload: unknown,
  fallbackMessage: string
): DashboardUiError | null => {
  if (isDashboardErrorResponse(payload)) {
    return {
      message: payload.error,
      code: payload.errorCode,
      recoveryActions: payload.recoveryActions ?? [],
      details: payload.details,
    };
  }

  if (isRecord(payload) && typeof payload.error === 'string') {
    return {
      message: looksTechnicalMessage(payload.error) ? fallbackMessage : payload.error,
      code:
        typeof payload.errorCode === 'string'
          ? (payload.errorCode as DashboardErrorCode)
          : null,
      recoveryActions: Array.isArray(payload.recoveryActions)
        ? payload.recoveryActions.filter(
            (entry): entry is DashboardRecoveryAction => typeof entry === 'string'
          )
        : [],
      details: payload.details,
    };
  }

  return null;
};

export const toDashboardUiError = async (
  error: unknown,
  fallbackMessage: string
): Promise<DashboardUiError> => {
  if (isRecord(error) && isResponseLike(error.context)) {
    const payload = await readResponsePayload(error.context);
    const parsed = buildUiErrorFromPayload(payload, fallbackMessage);
    if (parsed) return parsed;
  }

  const message = error instanceof Error ? error.message : null;
  return {
    message: toSafeUiMessage(message, fallbackMessage),
    code: null,
    recoveryActions: [],
  };
};
